/**
 * Placeholder workflow preview card rendered in chat for `workflow_preview` send_to_ui messages.
 *
 * Displays the rendered Slack message and a row-count badge from the dry-run results,
 * plus an "Open in editor" link when the workflow is saved (workflowId present).
 *
 * The editor link is built CLIENT-SIDE from workflowId + the workspace slug
 * (`ws.wp(...)`), never from a URL carried in the payload. The payload is authored
 * by the agent (LLM-controlled, influenceable by prompt injection), so a payload
 * URL is untrusted — rendering it in an href would be a `javascript:`-scheme XSS
 * sink. Deriving a relative path locally removes the trust boundary entirely.
 *
 * P12 replaces this placeholder with the full WorkflowPreviewBlock card chain.
 */
import { Link } from 'react-router-dom';

import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';
import type { WorkflowPreviewPayload } from '@/workflow/types';

interface WorkflowPreviewBlockProps {
  content: string;
}

export function WorkflowPreviewBlock({ content }: WorkflowPreviewBlockProps) {
  const ws = useWorkspaceOrNull();
  let payload: WorkflowPreviewPayload | null = null;
  try {
    payload = JSON.parse(content) as WorkflowPreviewPayload;
  } catch {
    // Malformed payload — fall through to error state below
  }

  if (!payload) {
    return (
      <div className="rounded-lg border border-border bg-card p-3 text-sm text-muted-foreground">
        Invalid workflow preview payload.
      </div>
    );
  }

  const renderedMessage = payload.dryRun?.slack?.renderedMessage;
  const rowCount = payload.dryRun?.query?.rowCount;

  return (
    <div className="rounded-lg border border-border bg-card p-3 text-sm" data-testid="workflow-preview-block">
      <div className="mb-2 flex items-center gap-2">
        <span className="font-medium">{payload.name}</span>
        {payload.status && (
          <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
            {payload.status}
          </span>
        )}
        {rowCount !== undefined && (
          <span
            className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            data-testid="workflow-preview-row-count"
          >
            {rowCount} {rowCount === 1 ? 'row' : 'rows'}
          </span>
        )}
      </div>
      {renderedMessage && (
        <div
          className="rounded border border-border bg-muted/40 p-2 font-mono text-xs whitespace-pre-wrap"
          data-testid="workflow-preview-message"
        >
          {renderedMessage}
        </div>
      )}
      {payload.workflowId && ws && (
        <div className="mt-2">
          <Link
            to={ws.wp(`/workflows/${encodeURIComponent(payload.workflowId)}`)}
            className="text-xs text-primary underline-offset-2 hover:underline"
            data-testid="workflow-preview-editor-link"
          >
            Open in editor
          </Link>
        </div>
      )}
    </div>
  );
}
